import { useMemo, useState, useEffect, useRef } from 'react';
import { getActiveTrace } from '@atlaskit/react-ufo/experience-trace-id-context';

import type { WhiteboardToken } from '@confluence/whiteboard-common/entry-points/whiteboard-shell-types.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useTokenService } from '@confluence/whiteboard-common/entry-points/useTokenService.ts';

import { useInitialUrl } from './useInitialUrl.ts';

interface Identity {
	accountId?: string | null;
	nickname?: string | null;
	picture?: string | null;
	email?: string | null;
	locale?: string | null;
}

export interface DatabaseBridgeDataProps {
	spaceKey?: string;
	contentId?: string;
	contentReferenceId?: string | null;
	spaceId?: string | null;
	spaceName?: string | null;
	title?: string | null;
	identity?: Identity | null;
	dataSecurityPolicyPageExport: boolean | null;
	dataSecurityPolicyAttachmentDownload: boolean | null;
	refetch?: () => void;
	version?: string;
	isSmartLinkEmbed?: boolean;
	versionId: string | null;
	presentationMode?: string;
	isNativeEmbedFullscreen?: boolean;
	bridgeClient?: string;
	isRovoParentProduct?: boolean;
}

export const useDatabaseBridgeData = ({
	spaceKey,
	contentId,
	contentReferenceId,
	spaceId,
	spaceName,
	title,
	identity,
	dataSecurityPolicyPageExport,
	dataSecurityPolicyAttachmentDownload,
	refetch,
	version,
	isSmartLinkEmbed,
	versionId,
	presentationMode,
	isNativeEmbedFullscreen,
	bridgeClient,
	isRovoParentProduct,
}: DatabaseBridgeDataProps) => {
	const [token, setToken] = useState<WhiteboardToken | undefined>(undefined);
	const { activationId, userId, cloudId } = useSessionData();
	const initialUrl = useInitialUrl({
		version,
		isSmartLinkEmbed,
		versionId,
		presentationMode,
		isNativeEmbedFullscreen,
		bridgeClient,
		isRovoParentProduct,
	});

	const tokenService = useTokenService({
		tenantId: cloudId,
		space: {
			id: spaceId,
		},
		referenceId: contentReferenceId,
		shareToken: undefined,
		contentId: contentId || '',
		dataSecurityPolicyPageExport: dataSecurityPolicyPageExport || null,
		dataSecurityPolicyAttachmentDownload: dataSecurityPolicyAttachmentDownload || null,
		refetch,
		documentType: 'database',
	});

	useEffect(() => {
		tokenService.onTokenChange((newToken) => {
			setToken(newToken);
		});
	}, [tokenService]);

	// Capture the active UFO trace on first render and freeze it in a ref.
	// The UFO platform clears the active trace (via clearActiveTrace()) when the transition
	// experience completes, which typically happens before the GraphQL query resolves.
	// By capturing it in a ref, we preserve the trace for the full lifetime of this hook.
	const capturedTraceRef = useRef<{ traceId: string; spanId: string } | null>(null);

	// Reset the captured trace when contentId changes (SPA transition to a different database),
	// so the new active trace is captured instead of keeping the stale one.
	const prevContentIdRef = useRef(contentId);
	if (prevContentIdRef.current !== contentId) {
		prevContentIdRef.current = contentId;
		capturedTraceRef.current = null;
	}

	if (capturedTraceRef.current === null) {
		const activeTrace = getActiveTrace();
		if (activeTrace?.traceId && activeTrace?.spanId) {
			capturedTraceRef.current = { traceId: activeTrace.traceId, spanId: activeTrace.spanId };
		}
	}
	const bridgeData = useMemo(
		() => ({
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId: spaceId ?? undefined,
			spaceName: spaceName ?? undefined,
			title: title ?? undefined,
			identity: identity ?? undefined,
			token,
			versionId,
			tracingContext: capturedTraceRef.current ?? undefined,
		}),
		[
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId,
			spaceName,
			title,
			identity,
			token,
			versionId,
			// capturedTraceRef.current is stable after first render, so no dependency needed
		],
	);
	return { bridgeData, initialUrl };
};
